import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";

import styled from "styled-components/macro";

import {
  faCalendar,
  faEnvelope,
  faEnvelopeOpenText,
  faExternalLink,
  faPhoneLaptop,
  faUsersMedical,
} from "@fortawesome/pro-solid-svg-icons";
import { useSignups } from "Signups/state";

import { getAmountInDollarsClean } from "Utilities/Amount/getAmountInDollarsClean";

const iconMap = {
  faEnvelope: faEnvelope,
  faExternalLink: faExternalLink,
  faUsersMedical: faUsersMedical,
  faPhoneLaptop: faPhoneLaptop,
  faEnvelopeOpenText: faEnvelopeOpenText,
  faCalendar: faCalendar,
};

export default function HistoryPopoverContent({
  historyData,
  respondentInfo,
  signupItemID,
  totalAmountPaid,
  hidePopover,
}) {
  const [state] = useSignups();
  const isSuggestedDonation =
    state.currentSignupList?.fundIt?.isSuggestedDonation;
  const isItemTypeSignup = state.currentSignupList?.signupListTypeID === 5;
  const currentUserCanViewGiving = state?.user?.canViewGiving;

  return (
    <div className="HistoryPopoverContent">
      <Content>
        {historyData.map((data, i) => (
          <Item
            key={i}
            signupListItemID={signupItemID}
            userInfo={respondentInfo}
            currentUserCanViewGiving={currentUserCanViewGiving}
            isSuggestedDonation={isSuggestedDonation}
            isItemTypeSignup={isItemTypeSignup}
            totalAmountPaid={totalAmountPaid}
            hidePopover={hidePopover}
            {...data}
          />
        ))}
      </Content>
    </div>
  );
}

const Content = styled.div`
  display: flex;
  flex-flow: column;

  .ItemWrapper:not(:last-child) {
    border-bottom: 1px solid #9ea9b3;
  }
`;

function Item({
  signupListItemID,
  userInfo,
  currentUserCanViewGiving,
  isSuggestedDonation,
  isItemTypeSignup,
  totalAmountPaid,
  hidePopover,
  ...data
}) {
  const paymentNoun = isSuggestedDonation ? "donation" : "payment";
  if (data.event.created.userInfo.fname === undefined)
    data.event.created.userInfo.fname = "";
  if (data.event.created.userInfo.lname === undefined)
    data.event.created.userInfo.lname = "";

  const openUserProfile = () => {
    window.fnReactBus.loadUserProfile(data.event.created.userInfo.ID);
    hidePopover();
  };

  const assembleItemString = () => {
    const isSameUser =
      parseInt(data.event.created.userInfo.ID) === parseInt(userInfo.ID);
    const isSameItem =
      parseInt(data.signupListItem.ID) === parseInt(signupListItemID);

    switch (data.event.ID) {
      case 1:
        return assembleCreatedString(isSameUser, isSameItem);
      case 2:
        return assembleEditedString(isSameUser, isSameItem);
      case 3:
        return assembleConfirmationEmailString(isSameUser, isSameItem);
      case 4:
        return assembleCancelledString(isSameUser, isSameItem);
      case 5:
        return assembleManualReminderSentString(isSameUser, isSameItem);
      case 6: {
        return assemblePaymentStatusChangeString({
          isSameUser,
          currentUserCanViewGiving,
          totalAmountPaid,
          userInfo,
        });
      }
      default:
        return "";
    }
  };

  const assembleCreatedString = (isSameUser, isSameItem) => {
    const createdUser = data.event.created.userInfo;
    if (createdUser.fname === undefined) createdUser.fname = "";
    if (createdUser.lname === undefined) createdUser.lname = "";
    if (!createdUser.fname.length && !createdUser.lname.length)
      createdUser.fname = "An admin";

    return isSameUser ? (
      `Signed up`
    ) : (
      <span>
        <span
          className={`can_mini_profile can_mini_profile_${createdUser.ID}`}
          data-userid={createdUser.ID}
          onClick={openUserProfile}
        >{`${createdUser.fname} ${createdUser.lname}`}</span>
        {` signed ${userInfo.fname} up`}
      </span>
    );
  };

  const assembleEditedString = (isSameUser, isSameItem) => {
    const { before, after } = data.event.details.slots;
    const { added, removed, autoDeleted } = data.event.details.additionalNames;
    const createdUser = data.event.created.userInfo;
    const slotsChanged = before !== after;

    if (createdUser.fname === undefined) createdUser.fname = "";
    if (createdUser.lname === undefined) createdUser.lname = "";
    if (!createdUser.fname.length && !createdUser.lname.length)
      createdUser.fname = "An admin";

    if (!slotsChanged && added.length < 1 && removed.length < 1)
      return `Edited Signup`;

    if (slotsChanged) {
      let str = isSameUser ? `Changed` : ` changed`;

      if (isItemTypeSignup) {
        str += ` the number of items from ${before} to ${after}`;
      } else {
        str += ` the number of people signed up from ${before} to ${after}`;
      }

      if (!isItemTypeSignup) {
        if (removed?.length)
          str += `. Removed ${listNames(removed)}${
            added.length ? ` and added ${listNames(added)}` : ``
          }`;
        else if (added?.length) str += `. Added ${listNames(added)}`;

        if (autoDeleted?.length)
          str += `. ${listNames(autoDeleted)} were automatically removed`;
      }

      str += `.`;

      return isSameUser ? (
        str
      ) : (
        <span>
          <span
            className={`can_mini_profile can_mini_profile_${createdUser.ID}`}
            data-userid={createdUser.ID}
            onClick={openUserProfile}
          >{`${createdUser.fname} ${createdUser.lname}`}</span>
          {str}
        </span>
      );
    } else if (removed.length) {
      let str = isSameUser ? `Removed` : ` removed`;

      str += ` ${listNames(removed)}${
        added.length ? ` and added ${listNames(added)}` : ``
      }`;

      return isSameUser ? (
        str
      ) : (
        <span>
          <span
            className={`can_mini_profile can_mini_profile_${createdUser.ID}`}
            data-userid={createdUser.ID}
            onClick={openUserProfile}
          >{`${createdUser.fname} ${createdUser.lname}`}</span>
          {str}
        </span>
      );
    } else {
      return isSameUser ? (
        `Added ${listNames(added)}.`
      ) : (
        <span>
          <span
            className={`can_mini_profile can_mini_profile_${createdUser.ID}`}
            data-userid={createdUser.ID}
            onClick={openUserProfile}
          >{`${createdUser.fname} ${createdUser.lname}`}</span>
          {` added ${listNames(added)}.`}
        </span>
      );
    }
  };

  const assembleConfirmationEmailString = (isSameUser, isSameItem) => {
    return `Confirmation email sent #${data.event.confirmationNumber}`;
  };

  const assembleCancelledString = (isSameUser, isSameItem) => {
    const createdUser = data.event.created.userInfo;
    if (createdUser.fname === undefined) createdUser.fname = "";
    if (createdUser.lname === undefined) createdUser.lname = "";
    if (!createdUser.fname.length && !createdUser.lname.length)
      createdUser.fname = "An admin";

    return isSameUser ? (
      `Cancelled their Signup`
    ) : (
      <span>
        <span
          className={`can_mini_profile can_mini_profile_${createdUser.ID}`}
          data-userid={createdUser.ID}
          onClick={openUserProfile}
        >{`${createdUser.fname} ${createdUser.lname}`}</span>
        {` cancelled ${userInfo.fname ? `${userInfo.fname}'s` : "the"} Signup`}
      </span>
    );
  };

  const assembleManualReminderSentString = (isSameUser, isSameItem) => {
    const createdUser = data.event.created.userInfo;
    if (createdUser.fname === undefined || createdUser.fname === null)
      createdUser.fname = "";
    if (createdUser.lname === undefined || createdUser.lname === null)
      createdUser.lname = "";
    if (!createdUser.fname?.length && !createdUser.lname?.length)
      createdUser.fname = "An admin";

    return isSameUser ? (
      `Was sent a Reminder text or email`
    ) : (
      <span>
        <span
          className={`can_mini_profile can_mini_profile_${createdUser.ID}`}
          data-userid={createdUser.ID}
          onClick={openUserProfile}
        >{`${createdUser.fname} ${createdUser.lname}`}</span>
        {` sent ${
          userInfo.fname ? `${userInfo.fname}` : "them"
        } a Reminder text/email`}
      </span>
    );
  };

  const assemblePaymentStatusChangeString = ({
    isSameUser,
    currentUserCanViewGiving,
    totalAmountPaid,
    userInfo,
  }) => {
    const createdUser = data.event.created.userInfo;
    if (createdUser.fname === undefined) createdUser.fname = "";
    if (createdUser.lname === undefined) createdUser.lname = "";
    let createdFullName = getFullName(createdUser.fname, createdUser.lname);
    // let respondentFullName = getFullName(userInfo.fname, userInfo.lname);
    // let respondentAndEventNameAreSame = respondentFullName === data.event.details.name;

    let isPayment = data.event.details.newStatus === "Paid";
    let isRefund =
      data.event.details.oldStatus === "Paid" &&
      data.event.details.newStatus === "Unpaid";
    let markedAsPaid =
      data.event.details.oldStatus === "Unpaid" &&
      data.event.details.newStatus === "MarkedAsPaid";
    let markedAsUnpaid =
      data.event.details.oldStatus === "MarkedAsPaid" &&
      data.event.details.newStatus === "Unpaid";
    let amount = data.event.details.amount;

    let payerUserID = data.event.details?.payerUserID;

    let affectedUserIsSameAsUserWhoDidTheMarking =
      (markedAsPaid || markedAsUnpaid) &&
      createdFullName === data.event.details.name;

    // Marked As Paid
    if (markedAsPaid) {
      return `${
        !isSameUser
          ? `${data.event.details.name} marked as paid by ${createdFullName}`
          : affectedUserIsSameAsUserWhoDidTheMarking
          ? "Marked themselves as paid"
          : `Marked ${data.event.details.name} as paid`
      }`;
    }
    // Marked As Paid
    if (markedAsUnpaid) {
      return `${
        !isSameUser
          ? `${data.event.details.name} marked as unpaid by ${createdFullName}`
          : affectedUserIsSameAsUserWhoDidTheMarking
          ? "Marked themselves as unpaid"
          : `Marked ${data.event.details.name} as unpaid `
      }`;
    }

    // payment by same user
    if (isSameUser && isPayment && !isRefund) {
      let contentString = `${
        amount
          ? `Paid $${getAmountInDollarsClean(amount)}`
          : `Made a ${paymentNoun}`
      }`;
      if (currentUserCanViewGiving) {
        return (
          <PaymentContent
            contentString={contentString}
            userID={payerUserID}
            hidePopover={hidePopover}
          />
        );
      }
      return contentString;
    }

    // refund to same user
    if (isSameUser && !isPayment && isRefund) {
      let contentString = `${
        amount
          ? `Refunded $${getAmountInDollarsClean(amount)}`
          : `Received a refund`
      }`;
      if (currentUserCanViewGiving) {
        return (
          <PaymentContent
            contentString={contentString}
            userID={payerUserID}
            hidePopover={hidePopover}
          />
        );
      }
      return contentString;
    }

    // payment by other user
    if (!isSameUser && isPayment && !isRefund) {
      let payingUser = createdFullName ? createdFullName : "Another user";
      let contentString = `${
        amount
          ? `${payingUser} paid $${getAmountInDollarsClean(amount)}`
          : `${payingUser} made a ${paymentNoun}`
      }`;
      if (currentUserCanViewGiving) {
        return (
          <PaymentContent
            contentString={contentString}
            userID={payerUserID}
            hidePopover={hidePopover}
          />
        );
      }
      return contentString;
    }

    // refund to other user
    if (!isSameUser && !isPayment && isRefund) {
      let refundInitiator = createdFullName
        ? createdFullName
        : "The user who paid";
      let contentString = `${
        amount
          ? `${refundInitiator} initiated a refund for $${getAmountInDollarsClean(
              amount
            )}`
          : `${refundInitiator} initiated a refund`
      }`;
      if (currentUserCanViewGiving) {
        return (
          <PaymentContent
            contentString={contentString}
            userID={payerUserID}
            hidePopover={hidePopover}
          />
        );
      }
      return contentString;
    }

    // per slot condensed payment
    if (
      data.event.details.costType === "PerSlot" &&
      data.event.details.newStatus === "Paid"
    ) {
      let isDonation = !data.event.details.amount;
      let contentString = `Paid $${getAmountInDollarsClean(totalAmountPaid)}`;
      if (isDonation) contentString = `Made a donation`;
      if (currentUserCanViewGiving) {
        return (
          <PaymentContent
            contentString={contentString}
            userID={payerUserID}
            hidePopover={hidePopover}
          />
        );
      }
      return contentString;
    }

    return "";
  };

  const listNames = (namesArr) => {
    namesArr.forEach((n, i) => {
      if (!namesArr[i].fname) namesArr[i].fname = "";
      if (!namesArr[i].lname) namesArr[i].lname = "";
    });

    switch (namesArr.length) {
      case 0:
        return `no one else`;
      case 1:
        return namesArr[0].fname + " " + namesArr[0].lname;
      case 2:
        return `${namesArr[0].fname} ${namesArr[0].lname} and ${namesArr[1].fname} ${namesArr[1].lname}`;
      default:
    }
    if (namesArr.length > 2) {
      const newArr = [];

      namesArr.forEach((nameObj) =>
        newArr.push(`${nameObj.fname} ${nameObj.lname}`)
      );

      const last = newArr.pop();
      return `${newArr.join(", ")} and ${last}`;
    }
  };

  const signedUpViaIcon = data?.event?.signedUpViaIcon;

  return (
    <ItemWrapper className="ItemWrapper">
      <div className="inside">
        <MidRow>
          <div className="icon-and-msg">
            <div>{assembleItemString()}</div>
          </div>
        </MidRow>
        <div className="bottom-row">
          <div className="date">{data.event.created.dateString}</div>
          <div className="icon-and-confirmation">
            {signedUpViaIcon && signedUpViaIcon in iconMap ? (
              <FontAwesomeIcon icon={iconMap[signedUpViaIcon]} />
            ) : null}
            &nbsp;
            <div className="ush-confirmation-number">
              #{data.event.confirmationNumber}
            </div>
          </div>
        </div>
      </div>
    </ItemWrapper>
  );
}

const ItemWrapper = styled.div`
  color: white;
  font-weight: 400;
  display: flex;
  flex-flow: column;
  padding: 6px 0;
  margin: 0 4px;
  text-align: left;

  font-size: 14px;

  &:hover {
    .bottom-row > .icon-and-confirmation {
      opacity: 1;
    }
  }

  .ush-confirmation-number {
    margin-right: 6px;
  }

  .ush-icon {
    display: flex;
    align-items: center;
  }

  .bottom-row {
    font-size: 12px;
    font-weight: 400;
    opacity: 0.8;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .icon-and-confirmation {
      display: flex;
      align-items: center;
      justify-content: space-between;
      opacity: 0;

      .ush-confirmation-number {
        margin-left: 6px;
        font-weight: 400;
      }
    }
  }
`;

const MidRow = styled.div`
  //  display: flex;
  align-items: center;
  justify-content: flex-start;
  //margin: 5px 0;
  margin-bottom: 2px;
  line-height: 16px;

  .icon-and-msg {
    display: flex;
    align-items: center;
  }

  .date {
    margin-top: 5px;
    font-size: 12px;
    opacity: 0.9;
    font-weight: 400;
  }

  .can_mini_profile {
    cursor: pointer;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 10px;
  padding: 0;
  cursor: pointer;
`;

function PaymentContent({ contentString, userID, hidePopover }) {
  function handleClick() {
    window.fnReactBus.goToProfileGivingTab(userID);
    hidePopover();
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      {contentString}{" "}
      {userID ? (
        <IconButton onClick={handleClick}>
          <i className="fal fa-link"></i>
        </IconButton>
      ) : null}
    </div>
  );
}

function getFullName(fname, lname) {
  return `${
    !fname && !lname
      ? ""
      : `${fname}${fname && lname ? ` ${lname}` : lname ? lname : ""}`
  }`;
}
